import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MailCheck } from 'lucide-react';
import { authApi } from '../api/auth';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/shared/ui/atoms/Card';
import { Input } from '@/shared/ui/atoms/Input';
import { Button } from '@/shared/ui/atoms/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/atoms/Form';

const confirmSchema = z.object({
  confirmation_code: z
    .string()
    .length(6, 'El código debe tener exactamente 6 dígitos')
    .regex(/^\d{6}$/, 'El código solo debe contener números'),
});

type ConfirmFormValues = z.infer<typeof confirmSchema>;

export default function ConfirmAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Email passed via navigate state from RegisterPage
  const email: string = location.state?.email ?? '';

  const confirmMutation = useMutation({
    mutationFn: (values: ConfirmFormValues) =>
      authApi.confirmSignUpCognito({ email, confirmation_code: values.confirmation_code }),
    onSuccess: () => {
      toast.success('¡Cuenta verificada! Ya puedes iniciar sesión.');
      navigate('/login');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Código inválido o expirado. Inténtalo de nuevo.';
      toast.error(message);
    },
  });

  const form = useForm<ConfirmFormValues>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { confirmation_code: '' },
  });

  function onSubmit(data: ConfirmFormValues) {
    if (!email) {
      toast.error('No se encontró el correo. Vuelve a registrarte.');
      navigate('/register');
      return;
    }
    confirmMutation.mutate(data);
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4 pt-safe pb-safe">
      <div className="w-full max-w-sm flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary">
            <MailCheck className="size-5 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Verifica tu cuenta</h1>
          <p className="text-sm text-muted-foreground">
            Hemos enviado un código de 6 dígitos a{' '}
            {email ? (
              <span className="font-medium text-foreground">{email}</span>
            ) : (
              'tu correo electrónico'
            )}
            . Revisa también la carpeta de spam.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Código de verificación</CardTitle>
            <CardDescription>Ingresa el código que recibiste por correo.</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="confirmation_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Código</FormLabel>
                      <FormControl>
                        <Input
                          id="confirmation_code"
                          placeholder="123456"
                          maxLength={6}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          className="text-center text-xl tracking-[0.5em] font-mono"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full mt-2"
                  disabled={confirmMutation.isPending}
                >
                  {confirmMutation.isPending ? 'Verificando…' : 'Verificar cuenta'}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              ¿No recibiste el código?{' '}
              <Link
                to="/register"
                className="text-primary underline-offset-4 hover:underline font-medium"
              >
                Volver al registro
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
